
import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { IconX } from './Icons';

interface ScannerProps {
  onScan: (sku: string) => void;
  isActive: boolean;
  onClose: () => void;
}

export const Scanner: React.FC<ScannerProps> = ({ onScan, isActive, onClose }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isActive) {
      // Pequeno delay para garantir que o DOM renderizou o elemento "reader"
      const timer = setTimeout(() => {
        startScanner();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
    }

    return () => {
      stopScanner();
    };
  }, [isActive]);

  const startScanner = async () => {
    try {
      if (scannerRef.current) return;

      const html5QrCode = new Html5Qrcode("reader", {
        formatsToSupport: [ 
          Html5QrcodeSupportedFormats.CODE_128, // Prioridade alta para etiquetas logísticas
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93
        ],
        verbose: false,
        experimentalFeatures: {
            useBarCodeDetectorIfSupported: true // Usa API nativa do Android/iOS (muito mais rápido para barcodes)
        }
      });
      scannerRef.current = html5QrCode;

      // Configuração dinâmica da caixa de leitura para favorecer códigos de barra largos
      const qrBoxFunction = (viewfinderWidth: number, viewfinderHeight: number) => {
        // Retângulo largo para guiar o usuário a enquadrar o código de barras corretamente
        const width = Math.floor(viewfinderWidth * 0.80); 
        const height = Math.floor(viewfinderHeight * 0.40); 
        
        // Limita a altura para parecer uma fenda de leitura de código de barras
        const constrainedHeight = height > 250 ? 250 : (height < 150 ? 150 : height);
        
        return {
          width: width,
          height: constrainedHeight
        };
      };

      await html5QrCode.start(
        { facingMode: "environment" }, // Voltar para configuração padrão (evita erro de OverconstrainedError)
        {
          fps: 10,
          qrbox: qrBoxFunction,
          aspectRatio: 1.0, 
          disableFlip: false,
          // FocoMode ajuda se o dispositivo suportar, mas é opcional na config padrão da lib
        },
        (decodedText) => {
          console.log("Scan success:", decodedText);
          onScan(decodedText);
        },
        (_) => {
          // Ignora erros de não detecção por frame
        }
      );
      setError(null);
    } catch (err) {
      console.error("Erro ao iniciar câmera", err);
      setError("Erro ao acessar câmera. Verifique permissões ou tente outro navegador.");
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (e) {
        console.error("Failed to stop scanner", e);
      }
      scannerRef.current = null;
    }
  };

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col items-center justify-center">
      <div className="absolute top-4 right-4 z-[70]">
        <button onClick={onClose} className="p-2 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors">
          <IconX className="w-8 h-8" />
        </button>
      </div>

      <div className="w-full h-full flex flex-col justify-center px-4 relative">
        <div id="reader" className="w-full rounded-lg overflow-hidden shadow-2xl border-2 border-blue-500/50 bg-black"></div>
        
        {error ? (
          <div className="mt-4 text-center text-red-400 bg-red-900/20 p-4 rounded backdrop-blur-sm">
            {error}
          </div>
        ) : (
          <div className="absolute bottom-12 left-0 right-0 text-center pointer-events-none px-6">
             <div className="inline-block bg-black/60 text-white px-4 py-2 rounded-xl backdrop-blur-sm text-sm font-medium border border-white/10 shadow-lg">
                Alinhe o código dentro da caixa
             </div>
          </div>
        )}
      </div>
    </div>
  );
};
